/**
 * Promo Utils - Apply system promo discounts to product pricing
 */

// Apply system promo discount to a price for a given supply month count
export const applyPromoDiscount = (price, systemPromo, months) => {
  if (!systemPromo?.applicableMonths || !price) return price;
  const discount = systemPromo.applicableMonths[String(months)] || 0;
  return discount > 0 ? Math.round(price * (1 - discount / 100)) : price;
};

// Apply a percentage discount to ONE stored rate while PRESERVING ITS SHAPE.
// `product_dosages.supply_pricing` values are tier-keyed objects — { access, one, plus } —
// not bare numbers (a bare number is the legacy flat shape; see LEARNINGS.md:1219). The old
// code multiplied the value directly, so a tier-keyed rate became NaN on any discounted month.
// Returning the same shape is what lets SupplyMonthSelector.resolvePrice still pick the
// viewer's tier downstream — that reader is correct and must not be changed. Board card 4082.
const calculateDiscountedRate = (rate, discount) => {
  if (!(discount > 0)) return rate;
  if (typeof rate === 'number') return Math.round(rate * (1 - discount / 100));
  if (rate && typeof rate === 'object') {
    return Object.fromEntries(
      Object.entries(rate).map(([tier, cents]) => [
        tier,
        typeof cents === 'number' ? Math.round(cents * (1 - discount / 100)) : cents
      ])
    );
  }
  return rate;
};

// Build adjusted supplyPricing with system promo discounts applied
export const getAdjustedSupplyPricing = (supplyPricing, basePrice, systemPromo) => {
  if (!systemPromo?.applicableMonths) return supplyPricing;

  const adjusted = {};

  if (supplyPricing && typeof supplyPricing === 'object') {
    for (const [months, price] of Object.entries(supplyPricing)) {
      const discount = systemPromo.applicableMonths[months] || 0;
      adjusted[months] = calculateDiscountedRate(price, discount);
    }
  }

  // Include base price for month 1 if not already in supplyPricing
  if (!adjusted['1'] && basePrice) {
    const discount = systemPromo.applicableMonths['1'] || 0;
    adjusted['1'] = discount > 0 ? Math.round(basePrice * (1 - discount / 100)) : basePrice;
  }

  return Object.keys(adjusted).length > 0 ? adjusted : supplyPricing;
};

// Check if a promo is a waiver type
export const isWaiverPromo = (promo) => promo?.discountType === 'waiver';

// Get the array of waived target strings from a waiver promo
export const getWaivedTargets = (promo) =>
  isWaiverPromo(promo) ? (promo?.waiverTargets || []) : [];
